import { FeatureError } from '@/libs/errors/FeatureError';
import { T } from '@/libs/translation';
import type { SnsTypeS } from '../../Sns/outbound/share';
import { AuthTypeS } from '../outbound/share';
import type { SignupErrorType } from '../type';

/** @description 로그인 페이지 쿼리가 유효하지 않는 경우*/
export const LOGIN_001 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.login.001_title'),
      message: T('auth.error.login.001_message'),
      code: 'LOGIN_001',
      priority: 10,
    },
    error,
  );
};

/** @description client의 redirect_url이 유효하지 않은 경우 */
export const LOGIN_002 = (error?: Error) =>
  new FeatureError(
    {
      title: T('auth.error.login.002_title'),
      message: T('auth.error.login.002_message'),
      code: 'LOGIN_002',
      priority: 3,
    },
    error,
  );

/** @description client가 존재하지 않는 경우 */
export const LOGIN_003 = (error?: Error) =>
  new FeatureError(
    {
      title: T('auth.error.login.003_title'),
      message: T('auth.error.login.003_message'),
      code: 'LOGIN_003',
      priority: 3,
    },
    error,
  );

/** @description 회원가입 요청 바디가 유효하지 않는 경우*/
export const SIGNUP_001 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.signup.001_title'),
      message: T('auth.error.signup.001_message'),
      code: 'SIGNUP_001',
      priority: 3,
    },
    error,
  );
};

/** @description 회원가입 요청시 이미 가입된 계정이 존재하는 경우 */
export const SIGNUP_002 = (extra: SnsTypeS.SnsType[], error?: Error) => {
  return new FeatureError<SignupErrorType>(
    {
      title: T('auth.error.signup.002_title'),
      message: T('auth.error.signup.002_message'),
      code: 'SIGNUP_002',
      extra,
    },
    error,
  );
};

/** @description 회원가입 요청시 동일한 이메일이 가입되어 있는 경우 */
export const SIGNUP_003 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.signup.003_title'),
      message: T('auth.error.signup.003_message'),
      code: 'SIGNUP_003',
    },
    error,
  );
};

/** @description 로그인 요청 바디가 유효하지 않는 경우*/
export const SIGNIN_001 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.signin.001_title'),
      message: T('auth.error.signin.001_message'),
      code: 'SIGNIN_001',
      priority: 1,
    },
    error,
  );
};

/** @description 가입된 user가 존재하지 않음*/
export const SIGNIN_002 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.signin.002_title'),
      message: T('auth.error.signin.002_message'),
      code: 'SIGNIN_002',
    },
    error,
  );
};

/** @description 가입은 되어있으나 비밀번호가 없는 경우*/
export const SIGNIN_003 = (extra: SnsTypeS.SnsType[], error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.signin.003_title'),
      message: T('auth.error.signin.003_message'),
      code: 'SIGNIN_003',
      extra,
    },
    error,
  );
};

/** @description 가입된 유저는 있지만 비밀번호가 다름*/
export const SIGNIN_004 = (error?: Error, json?: { title: string }) => {
  return new FeatureError(
    {
      title: json?.title ?? T('auth.error.signin.004_title'),
      message: T('auth.error.signin.004_message'),
      code: 'SIGNIN_004',
    },
    error,
  );
};

/** @description req.body에 authorization_code가 없는 경우*/
export const TOKEN_001 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.token.001_title'),
      message: T('auth.error.token.001_message'),
      code: 'TOKEN_001',
      priority: 10,
    },
    error,
  );
};

/** @description authorization_code가 db에 없거나 만료됨*/
export const TOKEN_002 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.token.002_title'),
      message: T('auth.error.token.002_message'),
      code: 'TOKEN_002',
      priority: 10,
    },
    error,
  );
};

/** @description authorization_code로 조회한 session이 정상적이지 않음*/
export const TOKEN_003 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.token.003_title'),
      message: T('auth.error.token.003_message'),
      code: 'TOKEN_003',
      priority: 3,
    },
    error,
  );
};

/** @description session에 저장된 user 데이터와 authorization_code에 등록된 유저 데이터가 맞지 않음*/
export const TOKEN_004 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.token.004_title'),
      message: T('auth.error.token.004_message'),
      code: 'TOKEN_004',
    },
    error,
  );
};

/** @description session에 저장된 client 데이터가 없거나 authorization_code가 맞지 않음*/
export const TOKEN_005 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.token.005_title'),
      message: T('auth.error.token.005_message'),
      code: 'TOKEN_005',
      priority: 10,
    },
    error,
  );
};

/** @description DB에 저장된 access_token의 유효기간이 만료되었거나 존재하지 않는다. */
export const TOKEN_006 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.token.006_title'),
      message: T('auth.error.token.006_message'),
      code: 'TOKEN_006',
      priority: 10,
    },
    error,
  );
};

/** @description DB에 저장된 refresh_token의 유효기간이 만료되었거나 존재하지 않는다. */
export const TOKEN_007 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.token.007_title'),
      message: T('auth.error.token.007_message'),
      code: 'TOKEN_007',
      priority: 10,
    },
    error,
  );
};

/** @description 유저 DB 조회 시 필요한 데이터를 주지 않음 */
export const USER_001 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.001_title'),
      message: T('auth.error.user.001_message'),
      code: 'USER_001',
      priority: 10,
    },
    error,
  );
};

/** @description req.body가 유효하지 않는 경우*/
export const USER_002 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.002_title'),
      message: T('auth.error.user.002_message'),
      code: 'USER_002',
      priority: 1,
    },
    error,
  );
};

/** @description user가 존재하지 않음 */
export const USER_003 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.003_title'),
      message: T('auth.error.user.003_message'),
      code: 'USER_003',
    },
    error,
  );
};

/** @description user가 존재하나 비밀번호가 없는 경우*/
export const USER_004 = (extra: AuthTypeS.USER_SNS_TYPE[], error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.004_title'),
      message: T('auth.error.user.004_message'),
      code: 'USER_004',
      extra,
    },
    error,
  );
};

/** @description user가 존재하나 비밀번호가 없는데 sns도 없는 경우 */
export const USER_004_01 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.004_01_title'),
      message: T('auth.error.user.004_01_message'),
      code: 'USER_004_01',
    },
    error,
  );
};

/** @description user_address를 만들기 위한 데이터가 없음*/
export const USER_005 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.005_title'),
      message: T('auth.error.user.005_message'),
      code: 'USER_005',
      priority: 3,
    },
    error,
  );
};

/** @description user 비밀번호 변경시 기존 비밀번호와 일치함 */
export const USER_006 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.006_title'),
      message: T('auth.error.user.006_message'),
      code: 'USER_006',
    },
    error,
  );
};

/** @description user uid 조회 시 authorization code가 없음 */
export const USER_007 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.007_title'),
      message: T('auth.error.user.007_message'),
      code: 'USER_007',
      priority: 10,
    },
    error,
  );
};

/** @description 비밀번호 변경시 현재 비밀번호가 존재하지만 요청 데이터에 현재 비밀번호가 없음 */
export const USER_008 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.008_title'),
      message: T('auth.error.user.008_message'),
      code: 'USER_008',
      priority: 3,
    },
    error,
  );
};

/** @description 비밀번호 변경시 현재 비밀번호 검증 실패 */
export const USER_009 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.009_title'),
      message: T('auth.error.user.009_message'),
      code: 'USER_009',
    },
    error,
  );
};

/** @description user 탈퇴 처리 중 탈퇴 처리한 user가 존재하지 않음 */
export const USER_010 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.010_title'),
      message: T('auth.error.user.010_message'),
      code: 'USER_010',
    },
    error,
  );
};

/** @description 동일한 이메일의 유저가 존재하는 경우 */
export const USER_011 = (extra: SnsTypeS.SnsType[], error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.011_title'),
      message: T('auth.error.user.011_message'),
      code: 'USER_011',
      extra,
    },
    error,
  );
};

/** @description user 탈퇴 처리 불가 */
export const USER_012 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user.012_title'),
      message: T('auth.error.user.012_message'),
      code: 'USER_012',
    },
    error,
  );
};

/** @description 멤버십 탈퇴 처리 불가 */
export const USER_013 = (error?: unknown) => {
  return new FeatureError(
    {
      title: T('auth.error.user.012_title'),
      message: T('auth.error.user.012_message'),
      code: 'USER_013',
    },
    error,
  );
};

/** @description 사이어티 탈퇴 처리 불가 */
export const USER_014 = (error?: unknown) => {
  return new FeatureError(
    {
      title: T('auth.error.user.012_title'),
      message: T('auth.error.user.012_message'),
      code: 'USER_014',
    },
    error,
  );
};

/** @description 유저의 주소 추가 및 변경시 요청 파라미터 타입 에러 */
export const USER_ADDRESS_001 = (error?: Error) => {
  return new FeatureError(
    {
      title: T('auth.error.user_address.001_title'),
      message: T('auth.error.user_address.001_description'),
      code: 'USER_ADDRESS_001',
      priority: 10,
    },
    error,
  );
};
